<template>
  <v-container fluid class="legal-page pa-0">
    <section class="hero d-flex align-center justify-center">
      <v-container>
        <v-row justify="center">
          <v-col cols="12" md="9" lg="8">
            <v-card class="legal-card pa-8">
              <v-btn
                text
                color="primary"
                class="mb-4"
                @click="goBack"
              >
                <v-icon left>mdi-arrow-left</v-icon>
                Volver
              </v-btn>

              <div class="text-center mb-6">
                <v-img
                  src="/logo.png"
                  contain
                  height="90"
                  class="mx-auto mb-4"
                />
                <h1 class="font-weight-bold">
                  Política de Privacidad de Kalendario
                </h1>
                <p class="effective-date">
                  Fecha de entrada en vigor: 15 de mayo de 2026 | Última actualización: 15 de mayo de 2026
                </p>
              </div>

              <div class="legal-text">
                <h2>1. Introducción</h2>
                <p>
                  En Kalendario, accesible desde
                  <a href="https://kalendario.thevalkiria.com" target="_blank" rel="noopener noreferrer">https://kalendario.thevalkiria.com</a>,
                  respetamos tu privacidad y nos comprometemos a proteger tus datos personales.
                </p>
                <p>
                  Esta Política de Privacidad explica qué datos recopilamos, con qué finalidad los usamos, cómo los almacenamos y protegemos, con quién podemos compartirlos y qué derechos tienes sobre tu información personal.
                </p>
                <p>
                  Esta política se aplica al uso de la aplicación y sitio web Kalendario.
                </p>
                <p>
                  Esta Política de Privacidad se ha elaborado de conformidad con el Reglamento (UE) 2016/679, General de Protección de Datos —RGPD—, y la Ley Orgánica 3/2018, de Protección de Datos Personales y garantía de los derechos digitales —LOPDGDD—.
                </p>
                <p>
                  Si tienes preguntas o deseas ejercer tus derechos de privacidad, puedes contactarnos en:
                  <a href="mailto:valkiria.lab.apps@gmail.com">valkiria.lab.apps@gmail.com</a>.
                </p>

                <h2>2. Responsable del Tratamiento</h2>
                <p>
                  El responsable del tratamiento de los datos personales recogidos a través de Kalendario es:
                </p>
                <ul>
                  <li><strong>Responsable:</strong> Sorraylla Ramos</li>
                  <li>
                    <strong>Correo electrónico:</strong>
                    <a href="mailto:valkiria.lab.apps@gmail.com">valkiria.lab.apps@gmail.com</a>
                  </li>
                  <li>
                    <strong>Sitio web:</strong>
                    <a href="https://kalendario.thevalkiria.com" target="_blank" rel="noopener noreferrer">https://kalendario.thevalkiria.com</a>
                  </li>
                  <li><strong>País:</strong> España</li>
                </ul>

                <h2>3. Definiciones</h2>
                <ul>
                  <li><strong>Datos personales:</strong> cualquier información que permita identificar directa o indirectamente a una persona, como nombre, dirección de correo electrónico o identificador de usuario.</li>
                  <li><strong>Tratamiento:</strong> cualquier operación realizada sobre datos personales, como recogida, almacenamiento, consulta, modificación, uso, comunicación o eliminación.</li>
                  <li><strong>Usuario:</strong> toda persona que accede o utiliza Kalendario.</li>
                  <li><strong>Servicio:</strong> la aplicación y sitio web Kalendario.</li>
                </ul>

                <h2>4. Datos que Recopilamos</h2>
                <p>
                  Kalendario recopila únicamente los datos necesarios para prestar, mantener, proteger y mejorar el servicio. Los datos personales tratados pueden incluir:
                </p>
                <ul>
                  <li><strong>Nombre y dirección de correo electrónico:</strong> obtenidos a través del inicio de sesión con cuenta de Google.</li>
                  <li><strong>Identificador de usuario:</strong> utilizado para asociar la cuenta del usuario con sus calendarios, eventos, grupos y preferencias.</li>
                  <li><strong>Eventos y calendarios creados:</strong> información introducida por el usuario para crear, consultar, modificar, eliminar o compartir calendarios y eventos.</li>
                  <li><strong>Grupos y relaciones entre usuarios:</strong> datos necesarios para gestionar grupos, miembros y calendarios compartidos.</li>
                  <li><strong>Preferencias básicas de uso:</strong> ajustes necesarios para personalizar la experiencia dentro del servicio.</li>
                  <li><strong>Datos técnicos y de uso:</strong> información básica generada por Firebase Analytics, como eventos de uso, interacciones dentro de la aplicación, tipo de dispositivo, sistema operativo, idioma, navegador, datos técnicos similares y métricas agregadas.</li>
                </ul>
                <p>
                  No solicitamos ni almacenamos contraseñas de Google. La autenticación se realiza mediante Firebase Authentication y Google como proveedor de identidad.
                </p>

                <h2>5. Datos Sensibles</h2>
                <p>
                  Kalendario no solicita categorías especiales de datos personales, como datos relativos a salud, ideología, religión, orientación sexual, afiliación sindical, datos biométricos o datos genéticos.
                </p>
                <p>
                  El usuario debe evitar introducir este tipo de información en calendarios, eventos, grupos o cualquier otro campo del servicio, salvo que sea estrictamente necesario y cuente con autorización suficiente para hacerlo, especialmente si se trata de información de terceros.
                </p>

                <h2>6. Finalidad del Tratamiento</h2>
                <div class="table-wrapper">
                  <table>
                    <thead>
                      <tr>
                        <th>Finalidad</th>
                        <th>Descripción</th>
                        <th>Base jurídica</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Autenticación segura</td>
                        <td>Permitir el acceso a Kalendario mediante cuenta de Google.</td>
                        <td>Ejecución del servicio solicitado y consentimiento.</td>
                      </tr>
                      <tr>
                        <td>Gestión de calendarios y eventos</td>
                        <td>Crear, mostrar, editar, eliminar y organizar calendarios y eventos del usuario.</td>
                        <td>Ejecución del servicio solicitado.</td>
                      </tr>
                      <tr>
                        <td>Calendarios y eventos compartidos</td>
                        <td>Permitir que varios usuarios colaboren o participen en grupos, eventos o calendarios comunes.</td>
                        <td>Ejecución del servicio solicitado e interés legítimo.</td>
                      </tr>
                      <tr>
                        <td>Sincronización entre miembros de un grupo</td>
                        <td>Mantener actualizada la información compartida entre usuarios pertenecientes a un mismo grupo.</td>
                        <td>Ejecución del servicio solicitado.</td>
                      </tr>
                      <tr>
                        <td>Notificaciones</td>
                        <td>Enviar avisos sobre cambios, nuevos eventos, actualizaciones o actividad relevante dentro del servicio.</td>
                        <td>Ejecución del servicio solicitado, interés legítimo y consentimiento cuando sea necesario.</td>
                      </tr>
                      <tr>
                        <td>Mantenimiento y mejora del servicio</td>
                        <td>Analizar el funcionamiento de Kalendario, detectar errores, mejorar la experiencia de usuario y optimizar funciones.</td>
                        <td>Interés legítimo y consentimiento cuando sea necesario.</td>
                      </tr>
                      <tr>
                        <td>Seguridad</td>
                        <td>Proteger las cuentas, prevenir usos indebidos, detectar actividad sospechosa y mantener la integridad del servicio.</td>
                        <td>Interés legítimo y obligación legal cuando corresponda.</td>
                      </tr>
                      <tr>
                        <td>Cumplimiento legal</td>
                        <td>Atender obligaciones legales, requerimientos de autoridades competentes o procedimientos legales aplicables.</td>
                        <td>Obligación legal.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h2>7. Servicios de Firebase Utilizados</h2>
                <p>
                  Kalendario utiliza servicios de Firebase, una plataforma proporcionada por Google, para alojar, autenticar, almacenar y analizar el uso del servicio.
                </p>
                <ul>
                  <li><strong>Firebase Hosting:</strong> alojamiento del sitio web y entrega segura del contenido.</li>
                  <li><strong>Firebase Authentication:</strong> gestión del inicio de sesión mediante cuenta de Google.</li>
                  <li><strong>Cloud Firestore:</strong> almacenamiento de datos de usuarios, calendarios, eventos, grupos, relaciones entre usuarios y preferencias básicas.</li>
                  <li><strong>Firebase Analytics:</strong> medición agregada del uso del servicio para entender cómo se utiliza Kalendario y mejorar su funcionamiento.</li>
                </ul>
                <p>
                  Google Firebase actúa como proveedor tecnológico y, en determinados servicios, como encargado del tratamiento, conforme a sus términos de procesamiento de datos y seguridad aplicables.
                </p>

                <h2>8. Inicio de Sesión con Google</h2>
                <p>
                  Para utilizar Kalendario, puedes iniciar sesión mediante tu cuenta de Google. En ese caso, recibimos únicamente la información necesaria para identificar tu cuenta dentro del servicio, normalmente:
                </p>
                <ul>
                  <li>Nombre asociado a tu cuenta de Google.</li>
                  <li>Dirección de correo electrónico.</li>
                  <li>Identificador único de usuario.</li>
                </ul>
                <p>
                  No tenemos acceso a tu contraseña de Google. Puedes revocar el acceso de Kalendario desde la configuración de seguridad de tu cuenta de Google.
                </p>

                <h2>9. Calendarios, Eventos y Grupos</h2>
                <p>
                  Kalendario permite crear y gestionar calendarios, eventos y grupos compartidos. Para ello, almacenamos la información que tú o los miembros de tu grupo introducís en el servicio.
                </p>
                <p>
                  Cuando participas en un grupo, ciertos datos pueden ser visibles para otros miembros del grupo, por ejemplo tu nombre, dirección de correo electrónico, eventos compartidos, calendarios asociados, relación con el grupo e información necesaria para la colaboración dentro del grupo.
                </p>
                <p>
                  La visibilidad de estos datos dependerá de las funciones disponibles en la aplicación y de la configuración o permisos aplicables. Eres responsable de no introducir en eventos, calendarios o grupos información sensible de terceros si no tienes autorización para hacerlo.
                </p>

                <h2>10. Notificaciones</h2>
                <p>
                  Kalendario puede enviarte notificaciones relacionadas con el funcionamiento del servicio, por ejemplo:
                </p>
                <ul>
                  <li>Cambios en eventos o calendarios compartidos.</li>
                  <li>Nuevos eventos creados por miembros de un grupo.</li>
                  <li>Actualizaciones relevantes de grupos o calendarios.</li>
                  <li>Avisos técnicos o de seguridad.</li>
                  <li>Información importante relacionada con tu cuenta o el servicio.</li>
                </ul>
                <p>
                  Cuando la ley lo exija, solicitaremos tu consentimiento para recibir determinados tipos de notificaciones. Puedes gestionar o desactivar notificaciones desde la configuración de tu dispositivo, navegador o aplicación, cuando esté disponible.
                </p>

                <h2>11. Analytics y Medición de Uso</h2>
                <p>
                  Utilizamos Firebase Analytics para obtener información agregada sobre el uso de Kalendario. Esta información nos ayuda a entender cómo se utiliza el servicio, detectar errores, mejorar funciones y optimizar la experiencia de usuario.
                </p>
                <p>
                  Los datos de analytics pueden incluir información técnica como tipo de dispositivo, sistema operativo, navegador, idioma, interacciones dentro de la aplicación, eventos de uso, métricas agregadas y datos técnicos similares.
                </p>
                <p>
                  No utilizamos Firebase Analytics para vender tus datos personales. No realizamos decisiones automatizadas, incluida la elaboración de perfiles, que produzcan efectos jurídicos sobre el usuario o le afecten significativamente de modo similar.
                </p>
                <p>
                  Utilizaremos Firebase Analytics únicamente cuando sea legalmente aplicable y, cuando sea necesario, tras obtener tu consentimiento. Puedes retirar o modificar tu consentimiento desde el banner o panel de configuración de cookies, cuando esté disponible.
                </p>

                <h2>12. Cookies y Tecnologías Similares</h2>
                <p>
                  Kalendario puede utilizar cookies, almacenamiento local u otras tecnologías similares necesarias para el funcionamiento del servicio, la autenticación, la seguridad y el análisis de uso. Las cookies o tecnologías analíticas no esenciales no se activarán sin consentimiento cuando así lo exija la normativa aplicable.
                </p>
                <p>
                  Puedes aceptar, rechazar o configurar las cookies no necesarias desde el banner o panel de configuración de cookies disponible en el sitio web. También puedes configurar tu navegador para bloquear o eliminar cookies. Sin embargo, algunas funciones de Kalendario podrían no funcionar correctamente si desactivas tecnologías necesarias para la autenticación, seguridad o funcionamiento básico del servicio.
                </p>

                <h2>13. Cómo Compartimos tus Datos</h2>
                <p>
                  No vendemos tus datos personales. Podemos compartir o permitir el acceso a datos personales únicamente en los siguientes casos:
                </p>
                <ul>
                  <li><strong>Proveedores de servicios:</strong> como Google Firebase, que nos proporciona infraestructura, autenticación, base de datos, alojamiento y analytics.</li>
                  <li><strong>Otros usuarios de tu grupo:</strong> cuando participas en calendarios, eventos o grupos compartidos, la información necesaria puede ser visible para esos miembros.</li>
                  <li><strong>Obligaciones legales:</strong> cuando sea necesario cumplir con una ley, requerimiento judicial, autoridad competente o procedimiento legal.</li>
                  <li><strong>Seguridad:</strong> para proteger los derechos, seguridad e integridad de Kalendario, sus usuarios o terceros.</li>
                  <li><strong>Con tu consentimiento:</strong> cuando nos autorices expresamente a compartir información para una finalidad concreta.</li>
                </ul>

                <h2>14. Conservación de los Datos</h2>
                <div class="table-wrapper">
                  <table>
                    <thead>
                      <tr>
                        <th>Tipo de dato</th>
                        <th>Periodo de conservación</th>
                        <th>Motivo</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Datos de cuenta</td>
                        <td>Mientras la cuenta permanezca activa.</td>
                        <td>Permitir el acceso y uso de Kalendario.</td>
                      </tr>
                      <tr>
                        <td>Calendarios y eventos</td>
                        <td>Mientras sean necesarios para el servicio o hasta que el usuario los elimine.</td>
                        <td>Gestión de calendarios y eventos.</td>
                      </tr>
                      <tr>
                        <td>Grupos y relaciones entre usuarios</td>
                        <td>Mientras el grupo exista o mientras el usuario forme parte de él.</td>
                        <td>Gestión de grupos y colaboración.</td>
                      </tr>
                      <tr>
                        <td>Preferencias básicas</td>
                        <td>Mientras la cuenta permanezca activa o hasta que sean modificadas o eliminadas.</td>
                        <td>Personalización del servicio.</td>
                      </tr>
                      <tr>
                        <td>Datos técnicos y analytics</td>
                        <td>Durante el periodo configurado en Firebase/Google Analytics.</td>
                        <td>Análisis, mantenimiento y mejora del servicio.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p>
                  Cuando elimines tu cuenta o solicites la supresión de tus datos, eliminaremos o anonimizaremos la información asociada, salvo que exista una obligación legal o una razón legítima para conservarla.
                </p>

                <h2>15. Eliminación de Cuenta y Datos Personales</h2>
                <p>
                  Puedes solicitar la eliminación completa de tu cuenta y de los datos personales asociados escribiendo a:
                  <a href="mailto:valkiria.lab.apps@gmail.com">valkiria.lab.apps@gmail.com</a>
                </p>
                <p>
                  Asunto recomendado: Kalendario - Eliminación de cuenta
                </p>
                <p>
                  Si la aplicación incorpora una opción de eliminación de cuenta desde ajustes, también podrás utilizarla directamente.
                </p>
                <p>
                  Una vez recibida la solicitud, podremos pedirte información adicional para verificar tu identidad antes de proceder a la eliminación. La eliminación podrá afectar al acceso a calendarios, eventos, grupos y demás información asociada a tu cuenta.
                </p>

                <h2>16. Seguridad de los Datos</h2>
                <p>
                  Aplicamos medidas técnicas y organizativas razonables para proteger los datos personales frente a accesos no autorizados, pérdida, alteración, divulgación o destrucción. Entre otras medidas, utilizamos:
                </p>
                <ul>
                  <li>Autenticación mediante Firebase Authentication.</li>
                  <li>Reglas de seguridad de Cloud Firestore.</li>
                  <li>Conexiones seguras mediante HTTPS/TLS.</li>
                  <li>Control de acceso a los datos según usuario, grupo o permisos.</li>
                  <li>Infraestructura proporcionada por Google Firebase.</li>
                  <li>Medidas de seguridad orientadas a prevenir accesos no autorizados o usos indebidos.</li>
                </ul>
                <p>
                  Aunque aplicamos medidas de seguridad adecuadas, ningún sistema conectado a Internet puede garantizar una seguridad absoluta.
                </p>

                <h2>17. Transferencias Internacionales de Datos</h2>
                <p>
                  Al utilizar servicios de Google Firebase, tus datos pueden ser tratados en servidores ubicados fuera de tu país de residencia, incluyendo países situados fuera del Espacio Económico Europeo.
                </p>
                <p>
                  Cuando sea aplicable, estas transferencias se realizan conforme a mecanismos legalmente reconocidos, como cláusulas contractuales tipo, decisiones de adecuación u otras garantías previstas por la normativa de protección de datos.
                </p>
                <p>
                  Puedes solicitar información adicional sobre las garantías aplicadas a las transferencias internacionales contactando con nosotros en:
                  <a href="mailto:valkiria.lab.apps@gmail.com">valkiria.lab.apps@gmail.com</a>.
                </p>

                <h2>18. Tus Derechos de Privacidad</h2>
                <p>
                  Dependiendo de tu lugar de residencia, puedes tener derecho a:
                </p>
                <ul>
                  <li>Acceder a tus datos personales.</li>
                  <li>Solicitar la rectificación de datos inexactos o incompletos.</li>
                  <li>Solicitar la eliminación de tus datos.</li>
                  <li>Solicitar la limitación del tratamiento.</li>
                  <li>Oponerte a determinados tratamientos.</li>
                  <li>Solicitar la portabilidad de tus datos.</li>
                  <li>Retirar tu consentimiento cuando el tratamiento se base en consentimiento.</li>
                  <li>Presentar una reclamación ante la autoridad de protección de datos competente.</li>
                </ul>
                <p>
                  Puedes ejercer estos derechos contactando con nosotros en:
                  <a href="mailto:valkiria.lab.apps@gmail.com">valkiria.lab.apps@gmail.com</a>.
                  Podremos pedirte información adicional para verificar tu identidad antes de atender una solicitud. Responderemos dentro de los plazos establecidos por la legislación aplicable.
                </p>

                <h2>19. Derechos de Usuarios en la Unión Europea y Espacio Económico Europeo</h2>
                <p>
                  Si te encuentras en la Unión Europea o en el Espacio Económico Europeo, el tratamiento de tus datos personales se rige por el Reglamento General de Protección de Datos —RGPD— y, en España, por la Ley Orgánica 3/2018 —LOPDGDD—.
                </p>
                <p>
                  Las bases jurídicas principales para el tratamiento son ejecución del servicio solicitado, consentimiento, interés legítimo y obligación legal. También tienes derecho a presentar una reclamación ante la autoridad de protección de datos de tu país. En España, puedes presentar una reclamación ante la Agencia Española de Protección de Datos —AEPD—.
                </p>

                <h2>20. Privacidad de Menores</h2>
                <p>
                  Kalendario no está dirigido a menores de 14 años en España ni a menores de la edad mínima equivalente en otras jurisdicciones, salvo consentimiento válido de sus representantes legales cuando sea aplicable.
                </p>
                <p>
                  No recopilamos conscientemente datos personales de menores que no cumplan la edad mínima aplicable. Si crees que un menor nos ha proporcionado datos personales sin autorización, contacta con nosotros en:
                  <a href="mailto:valkiria.lab.apps@gmail.com">valkiria.lab.apps@gmail.com</a>.
                  Revisaremos la situación y eliminaremos la información correspondiente si procede.
                </p>

                <h2>21. Enlaces a Sitios de Terceros</h2>
                <p>
                  Kalendario puede contener enlaces a sitios web o servicios de terceros. No somos responsables de las prácticas de privacidad, contenido o seguridad de dichos sitios externos. Te recomendamos revisar sus propias políticas de privacidad antes de facilitarles datos personales.
                </p>

                <h2>22. Cambios en esta Política de Privacidad</h2>
                <p>
                  Podemos actualizar esta Política de Privacidad periódicamente para reflejar cambios legales, técnicos o funcionales. Cuando realicemos cambios importantes, actualizaremos la fecha de “Última actualización” y, si corresponde, mostraremos un aviso destacado en el servicio.
                </p>
                <p>
                  La versión actualizada será aplicable desde su publicación o desde la fecha indicada como entrada en vigor. Te recomendamos revisar esta Política de Privacidad periódicamente.
                </p>

                <h2>23. Contacto</h2>
                <p>
                  Para preguntas, solicitudes relacionadas con tus datos personales o reclamaciones de privacidad, puedes contactar con nosotros:
                </p>
                <ul>
                  <li>
                    <strong>Sitio web:</strong>
                    <a href="https://kalendario.thevalkiria.com" target="_blank" rel="noopener noreferrer">https://kalendario.thevalkiria.com</a>
                  </li>
                  <li>
                    <strong>Email:</strong>
                    <a href="mailto:valkiria.lab.apps@gmail.com">valkiria.lab.apps@gmail.com</a>
                  </li>
                  <li><strong>Responsable:</strong> Sorraylla Ramos</li>
                </ul>
                <p>
                  Intentaremos responder a las consultas generales en un plazo razonable y a las solicitudes formales de protección de datos dentro de los plazos establecidos por la legislación aplicable.
                </p>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>
  </v-container>
</template>

<script>
export default {
  name: 'PrivacyPolicy',
  methods: {
    goBack() {
      if (window.history.length > 1) {
        this.$router.back();
        return;
      }

      this.$router.push("/");
    }
  }
}
</script>

<style scoped>
.legal-page {
  min-height: 100vh;
}

.hero {
  min-height: 100vh;
}

.legal-card {
  border-radius: 10px;
  box-shadow: 0 30px 70px rgba(0, 0, 0, 0.25);
}

.effective-date {
  color: #666;
  font-size: 0.95rem;
  margin-top: 12px;
}

.legal-text p,
.legal-text li {
  color: #444;
  line-height: 1.7;
}

.legal-text p {
  margin-bottom: 16px;
}

.legal-text ul {
  margin-bottom: 16px;
  padding-left: 22px;
}

.legal-text li {
  margin-bottom: 8px;
}

.legal-text h2 {
  margin-top: 28px;
  margin-bottom: 10px;
  font-size: 1.35rem;
  font-weight: 700;
}

.legal-text a {
  color: #4f46e5;
  font-weight: 600;
  text-decoration: none;
  overflow-wrap: anywhere;
}

.legal-text a:hover {
  text-decoration: underline;
}

.table-wrapper {
  margin: 16px 0 22px;
  overflow-x: auto;
}

.legal-text table {
  width: 100%;
  min-width: 680px;
  border-collapse: collapse;
  color: #444;
}

.legal-text th,
.legal-text td {
  border: 1px solid #e5e7eb;
  padding: 12px;
  text-align: left;
  vertical-align: top;
  line-height: 1.55;
}

.legal-text th {
  background: #f7f7fb;
  color: #222;
  font-weight: 700;
}

.legal-text td:first-child {
  font-weight: 600;
}
</style>
